import { Box, Paper, Typography, TextField, Button, Avatar, CircularProgress } from "@mui/material";
import React from "react";
import Cookies from "js-cookie";

import Topbar from "../components/topbar";
import { useUser } from "../providers/user-provider";
import { useSettings } from "../providers/settings-provider";
import { useToast } from "../providers/toaster-provider";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { formatDate } from "../util/date";

type ProfileResponse = { firstName: string; lastName: string; email: string; loginMethods: string[] };

const PROVIDER_LABELS: Record<string, string> = { google: 'Google', microsoft: 'Microsoft', apple: 'Apple' };

const loginMethodLabel = (methods: string[] | undefined): string => {
    if (!methods) return '';
    if (methods.length === 0) return 'Email & password';
    return methods.map(m => PROVIDER_LABELS[m.toLowerCase()] ?? m).join(', ');
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography fontWeight={500} fontSize={14} mb={0.5} color="var(--color-text-muted)">{label}</Typography>
        <Typography fontSize={15}>{value ?? '—'}</Typography>
    </Box>
);

const ProfileSettings: React.FC = () => {
    const { user, setUser } = useUser();
    const { companySettings } = useSettings();
    const { showSuccess, showError } = useToast();

    const [fetchProfile, setFetchProfile] = useApi<ProfileResponse>();
    const [saveProfile, setSaveProfile] = useApi<{ firstName: string; lastName: string }>();

    // Saved values (shown in the header) vs. the editable draft in the fields.
    const [name, setName] = React.useState({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' });
    const [draft, setDraft] = React.useState(name);
    const [editing, setEditing] = React.useState(false);
    const email = fetchProfile.data?.email ?? user?.email ?? '';

    React.useEffect(() => {
        document.title = "FineAnts | Profile";
        setFetchProfile(buildApiRequestConfig(routes.user.fetchProfile));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const { data, error } = fetchProfile;
        if (data) {
            const loaded = { firstName: data.firstName, lastName: data.lastName };
            setName(loaded);
            setDraft(loaded);
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to load your profile');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchProfile.data, fetchProfile.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = saveProfile;
        if (data && !isLoading) {
            const saved = { firstName: data.firstName, lastName: data.lastName };
            setName(saved);
            setDraft(saved);
            setEditing(false);
            // The rest of the app (account menu, checkout) reads the name from
            // the user cookie. Kept for 30 days: AuthGuard needs BOTH the user
            // and token cookies, so a session-only login still ends with its
            // token cookie when the browser closes - this never extends it.
            if (user) {
                const updatedUser = { ...user, ...saved };
                Cookies.set('user', JSON.stringify(updatedUser), { expires: 30 });
                setUser(updatedUser);
            }
            showSuccess('Profile updated');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to update your profile');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveProfile.data, saveProfile.isLoading, saveProfile.error]);

    const draftValid = draft.firstName.trim() !== '' && draft.lastName.trim() !== '';
    const isDirty = draft.firstName.trim() !== name.firstName || draft.lastName.trim() !== name.lastName;

    const handleSave = () => {
        if (!draftValid) return;
        setSaveProfile({
            ...buildApiRequestConfig(routes.user.updateProfile),
            body: { firstName: draft.firstName.trim(), lastName: draft.lastName.trim() },
        });
    };

    const handleCancel = () => {
        setDraft(name);
        setEditing(false);
    };

    const displayName = `${name.firstName} ${name.lastName}`.trim();
    const buttonSx = { borderRadius: 2, px: 4, fontWeight: 600, textTransform: 'none' };

    return (
        <div style={{ width: '100%' }}>
            <Topbar />
            <div style={{ padding: '10px' }}>
                <Paper
                    sx={{
                        background: 'var(--color-page-background)',
                        width: '100%',
                        padding: 4,
                        boxSizing: 'border-box',
                        borderRadius: 0,
                        paddingBottom: 12
                    }}
                >
                    <Box mb={2}>
                        <Typography variant="h6" color='var(--color-text-muted)'>Profile</Typography>
                    </Box>
                    {/* Top section: Avatar, name, email, edit button */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 4, maxWidth: 1000 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar src="/avatar1.png" sx={{ width: 64, height: 64 }} />
                            <Box>
                                <Typography fontWeight={600} fontSize={18}>{displayName || 'Add your name'}</Typography>
                                <Typography color="var(--color-text-muted)" fontSize={15}>{email}</Typography>
                            </Box>
                        </Box>
                        {editing ? (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={saveProfile.isLoading}
                                    sx={{ ...buttonSx, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    disabled={!draftValid || !isDirty || saveProfile.isLoading}
                                    sx={{ ...buttonSx, bgcolor: 'var(--color-primary)', color: '#fff' }}
                                >
                                    {saveProfile.isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                                </Button>
                            </Box>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => setEditing(true)}
                                disabled={fetchProfile.isLoading}
                                sx={{ ...buttonSx, bgcolor: 'var(--color-primary)', color: '#fff' }}
                            >
                                Edit
                            </Button>
                        )}
                    </Box>
                    {/* Main form fields - read-only until Edit is clicked */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, maxWidth: 1000 }}>
                        <Box flex={1} display="flex" flexDirection="column" gap={3}>
                            <Box>
                                <Typography fontWeight={500} fontSize={14} mb={0.5}>First Name</Typography>
                                <TextField
                                    placeholder="Your First Name"
                                    fullWidth
                                    size="small"
                                    value={draft.firstName}
                                    onChange={(e) => setDraft(d => ({ ...d, firstName: e.target.value }))}
                                    disabled={!editing}
                                    error={editing && draft.firstName.trim() === ''}
                                    helperText={editing && draft.firstName.trim() === '' ? 'First name is required' : undefined}
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Box>
                        </Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={3}>
                            <Box>
                                <Typography fontWeight={500} fontSize={14} mb={0.5}>
                                    Last Name
                                </Typography>
                                <TextField
                                    placeholder="Your Last Name"
                                    fullWidth
                                    size="small"
                                    value={draft.lastName}
                                    onChange={(e) => setDraft(d => ({ ...d, lastName: e.target.value }))}
                                    disabled={!editing}
                                    error={editing && draft.lastName.trim() === ''}
                                    helperText={editing && draft.lastName.trim() === '' ? 'Last name is required' : undefined}
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Box>
                            <Box>
                                <Typography fontWeight={500} fontSize={14} mb={0.5}>
                                    Login Method
                                </Typography>
                                <TextField value={loginMethodLabel(fetchProfile.data?.loginMethods)} fullWidth size="small" disabled />
                            </Box>
                        </Box>
                    </Box>

                    <Box mt={5} mb={3}>
                        <Typography variant="h6" color="var(--color-text-muted)">Company Details</Typography>
                    </Box>
                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr 1fr', sm: '1fr 1fr 1fr' }}
                        gap={4}
                        maxWidth={1000}
                    >
                        <DetailField label="Company Name" value={companySettings?.company.name} />
                        <DetailField label="Business Type" value={companySettings?.company.businessType} />
                        <DetailField label="Contact Number" value={companySettings?.company.contactNumber} />
                        <DetailField label="Member Since" value={companySettings?.company.createdAt ? formatDate(companySettings.company.createdAt) : undefined} />
                    </Box>
                </Paper>
            </div>
        </div>
    );
};

export default ProfileSettings;